import ufeApi from 'services/api/ufeApi';
import selfReturnUtils from 'utils/SelfReturn';
import { gqlClient } from 'services/api/gql/gqlClient';
import { AddSamplesToReplacementOrder } from 'constants/gql/mutations/checkout/addSamplesToReplacementOrder.gql';

// https://confluence.sephora.com/wiki/pages/viewpage.action?pageId=275023507

/**
 * Adds samples to a replacement (NCR) order.
 * Routes to OIS GraphQL (AddSamplesToReplacementOrder) or legacy OXS REST based on oisMigration feature flags.
 *
 * @param {Object} params - The add samples payload
 * @param {string[]} params.sampleSkuIdList - List of sample SKU IDs to add
 * @param {string} params.replacementOrderId - The replacement order ID
 * @returns {Promise<Object>} The add samples response
 * @throws {Error} Throws with errors array for OIS GraphQL errors field responses
 * @throws {Error} Throws when OIS returns a non-SUCCESS responseCode
 * @throws {Object} Throws raw error object for OXS errors (handled by ufeApi)
 */
const addSamplesToReplacementOrder = async params => {
    const originatingOrderId = selfReturnUtils.getOriginalOrderId();

    const globalEnabled = Sephora?.configurationSettings?.oisMigration?.global?.isEnabled;
    const returnsEnabled = Sephora?.configurationSettings?.oisMigration?.returns?.isEnabled;

    if (globalEnabled && returnsEnabled) {
        const input = {
            replacementOrderId: params.replacementOrderId,
            samples: params.sampleSkuIdList
        };

        if (originatingOrderId) {
            input.originalOrderId = originatingOrderId;
        }

        const data = await gqlClient.query({
            operationName: 'AddSamplesToReplacementOrder',
            query: AddSamplesToReplacementOrder,
            variables: { input }
        });

        const result = data?.addSamplesToReplacementOrder;

        if (!result) {
            throw new Error('Add samples to replacement order: unexpected response shape');
        }

        if (result.errors?.length) {
            const error = new Error(result.errors[0]?.message || 'Add samples to replacement order failed');
            error.errorCode = -1;
            error.errorMessages = result.errors.map(e => e?.message).filter(Boolean);
            error.errors = result.errors;
            throw error;
        }

        if (result.responseCode && result.responseCode !== 'SUCCESS') {
            const error = new Error(result.message || 'Add samples to replacement order failed');
            error.errorCode = -1;
            error.responseCode = result.responseCode;
            error.errorMessages = result.message ? [result.message] : [];
            throw error;
        }

        return result;
    }

    const originalOrderIdParam = originatingOrderId ? `?originalOrderId=${originatingOrderId}` : '';
    const url = '/api/selfReturn/orders/samplesToReplacementOrder' + originalOrderIdParam;

    return ufeApi.makeRequest(url, { method: 'POST', body: JSON.stringify(params) }, { skipLoginPrompt: true });
};

export default addSamplesToReplacementOrder;
